import { motion } from 'framer-motion';
import { TrendingUp, Package, ShoppingBag, Star } from 'lucide-react';
import { useHomeStats } from '@/hooks/useApi.js';

export function HomeStats() {
  const { data: stats, isLoading, error } = useHomeStats();

  if (isLoading || error || !stats) {
    return null; // Don't show anything if loading or error
  }

  const statsData = [
    {
      icon: Package,
      label: 'Total Products',
      value: stats.totalProducts || 0,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      icon: ShoppingBag,
      label: 'Total Orders',
      value: stats.totalOrders || 0,
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      icon: TrendingUp,
      label: 'Happy Customers',
      value: stats.totalCustomers || 0,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    },
    {
      icon: Star,
      label: 'Average Rating',
      value: stats.averageRating ? `${stats.averageRating}★` : '0★',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
  ];

  return (
    <section className="py-12 md:py-16 bg-secondary/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Our <span className="text-gradient-rainbow">Journey</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Growing together with fashion lovers across India
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {statsData.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full ${stat.bgColor} mb-4`}>
                <stat.icon className={`h-8 w-8 md:h-10 md:w-10 ${stat.color}`} />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </div>
              <p className="text-muted-foreground text-sm md:text-base font-medium">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Additional highlight for new arrivals and this week's focus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 pt-8 border-t border-border/50"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                This Week
              </div>
              <p className="text-muted-foreground">
                Fresh arrivals and trending styles
              </p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {stats.totalReviews || 0}+
              </div>
              <p className="text-muted-foreground">
                Customer reviews and counting
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}